// Tenantra Inc. - Privacy Policy (TransUnion-style industry standard formatting)
import { ArrowLeft, Building2, Shield, Printer } from 'lucide-react';
import { Logo } from '@/components/Logo';
import { useState, useRef } from 'react';

const TOC_ITEMS = [
  { id: 'privacy-facts', label: 'Privacy Facts Notice' },
  { id: 'introduction', label: '1. Introduction' },
  { id: 'scope-application', label: '2. Scope and Application' },
  { id: 'platform-role', label: '3. Platform Role' },
  { id: 'information-we-collect', label: '4. Information We Collect' },
  { id: 'how-we-collect', label: '5. How and Why We Collect' },
  { id: 'consent', label: '6. Consent' },
  { id: 'disputes-corrections', label: '7. Disputes and Corrections' },
  { id: 'third-party-disclosure', label: '8. Third-Party Disclosure' },
  { id: 'data-storage-security', label: '9. Data Storage and Security' },
  { id: 'data-retention', label: '10. Data Retention' },
  { id: 'your-rights', label: '11. Your Rights Under PIPEDA' },
  { id: 'cookies-tracking', label: '12. Cookies and Tracking' },
  { id: 'use-of-information', label: '13. Use of Information' },
  { id: 'sms-messaging', label: '14. SMS Text Messaging' },
  { id: 'changes', label: '15. Changes to This Policy' },
  { id: 'contact-us', label: '16. Contact Us' },
];

export default function PrivacyPolicyPage() {
  const [activeSection, setActiveSection] = useState('');
  const contentRef = useRef(null);

  const scrollToSection = (id) => {
    const el = document.getElementById(id);
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'start' });
      setActiveSection(id);
    }
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <header className="bg-white border-b border-stone-200 sticky top-0 z-40 print:hidden">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 py-3.5">
          <div className="flex items-center justify-between">
            <Logo size="md" />
            <div className="flex items-center gap-3">
              <button
                onClick={() => window.print()}
                className="text-xs text-stone-500 hover:text-stone-700 flex items-center gap-1.5 px-3 py-1.5 border border-stone-200 rounded-lg hover:bg-stone-50 transition-colors"
                data-testid="print-btn"
              >
                <Printer className="w-3.5 h-3.5" />
                Print
              </button>
              <a href="/" className="text-sm text-stone-500 hover:text-teal-600 flex items-center gap-1.5 transition-colors" data-testid="back-home-link">
                <ArrowLeft className="w-4 h-4" />
                <span className="hidden sm:inline">Back to Home</span>
              </a>
            </div>
          </div>
        </div>
      </header>

      {/* Main layout */}
      <div className="max-w-5xl mx-auto px-4 sm:px-6 py-8 sm:py-10 print:py-4 print:max-w-none">
        <div className="lg:grid lg:grid-cols-[220px_1fr] lg:gap-10">

          {/* Sidebar TOC */}
          <aside className="hidden lg:block print:hidden">
            <nav className="sticky top-20">
              <p className="text-[11px] font-semibold uppercase tracking-wider text-stone-400 mb-3">On this page</p>
              <ul className="space-y-0.5 max-h-[calc(100vh-120px)] overflow-y-auto pr-1">
                {TOC_ITEMS.map((item) => (
                  <li key={item.id}>
                    <button
                      onClick={() => scrollToSection(item.id)}
                      className={`block w-full text-left text-[13px] leading-snug py-1.5 px-2.5 rounded transition-colors ${
                        activeSection === item.id
                          ? 'text-teal-700 bg-teal-50 font-medium'
                          : 'text-stone-500 hover:text-stone-800 hover:bg-stone-50'
                      }`}
                    >
                      {item.label}
                    </button>
                  </li>
                ))}
              </ul>
            </nav>
          </aside>

          {/* Document body */}
          <article ref={contentRef}>
            {/* Title block */}
            <div className="mb-8 sm:mb-10 pb-6 border-b border-stone-200">
              <div className="flex items-center gap-2 mb-3">
                <Shield className="w-5 h-5 text-teal-600" />
                <span className="text-xs font-semibold uppercase tracking-wider text-teal-600">Legal</span>
              </div>
              <h1 className="text-3xl sm:text-4xl font-bold text-stone-900 mb-2" data-testid="privacy-title">Privacy Policy</h1>
              <p className="text-sm text-stone-500">(Last updated April 2026)</p>
            </div>

            {/* ===== PRIVACY FACTS NOTICE ===== */}
            <section id="privacy-facts" className="mb-10 scroll-mt-24">
              <div className="bg-stone-50 border border-stone-200 rounded-lg overflow-hidden">
                <div className="bg-stone-800 px-5 py-3">
                  <h2 className="text-sm font-bold text-white uppercase tracking-wide">Tenantra Inc. Privacy Facts Notice</h2>
                </div>
                <div className="px-5 py-5 space-y-5">
                  <div>
                    <h3 className="text-xs font-bold uppercase tracking-wider text-stone-500 mb-1.5">Scope</h3>
                    <p className="text-sm text-stone-700 leading-relaxed">
                      This Privacy Policy explains how Tenantra Inc. collects, uses, discloses, and safeguards your information when you use our tenant reporting platform and related services. It applies to landlords, property managers, tenants, and website visitors.
                    </p>
                  </div>
                  <div>
                    <h3 className="text-xs font-bold uppercase tracking-wider text-stone-500 mb-1.5">Personal Information</h3>
                    <ul className="text-sm text-stone-700 space-y-1 list-disc pl-5">
                      <li>We collect account, landlord, tenant, and technical information as needed to operate the platform.</li>
                      <li>Tenants are only onboarded after providing explicit consent.</li>
                      <li>Tenantra does not independently verify all submitted information.</li>
                    </ul>
                  </div>
                  <div>
                    <h3 className="text-xs font-bold uppercase tracking-wider text-stone-500 mb-1.5">Uses</h3>
                    <ul className="text-sm text-stone-700 space-y-1 list-disc pl-5">
                      <li>Service delivery, identity verification, platform improvement, and legal compliance</li>
                      <li>Tenantra is not responsible for decisions made based on platform data</li>
                    </ul>
                  </div>
                  <div>
                    <h3 className="text-xs font-bold uppercase tracking-wider text-stone-500 mb-1.5">Your Rights</h3>
                    <ul className="text-sm text-stone-700 space-y-1 list-disc pl-5">
                      <li>Access, correct, or withdraw consent for your personal data</li>
                      <li>Dispute rental reports and file complaints</li>
                      <li>Contact: <strong>privacy@tenantra.ca</strong></li>
                    </ul>
                  </div>
                  <p className="text-xs text-stone-500 pt-2 border-t border-stone-200">
                    For more detailed information, please review the full Privacy Policy below.
                  </p>
                </div>
              </div>
            </section>

            {/* ===== FULL PRIVACY POLICY ===== */}

            {/* 1. Introduction */}
            <section id="introduction" className="mb-9 scroll-mt-24">
              <h2 className="text-lg font-bold text-stone-900 mb-3 pb-2 border-b border-stone-200">1. Introduction</h2>
              <p className="text-sm text-stone-700 leading-relaxed mb-3">
                Tenantra Inc. ("<strong>Tenantra</strong>", "<strong>we</strong>", "<strong>us</strong>", or "<strong>our</strong>") is committed to protecting the privacy and security of personal information. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you use our tenant reporting platform and related services.
              </p>
              <p className="text-sm text-stone-700 leading-relaxed">
                This policy applies to all users of our platform, including landlords, property managers, tenants, and visitors to our website.
              </p>
            </section>

            {/* 2. Scope and Application */}
            <section id="scope-application" className="mb-9 scroll-mt-24">
              <h2 className="text-lg font-bold text-stone-900 mb-3 pb-2 border-b border-stone-200">2. Scope and Application</h2>
              <p className="text-sm text-stone-700 leading-relaxed mb-3">This Privacy Policy applies to:</p>
              <ul className="list-disc pl-5 text-sm text-stone-700 space-y-1.5 mb-4">
                <li>Personal information collected through our website (tenantra.ca)</li>
                <li>Information collected through our landlord and tenant onboarding processes</li>
                <li>Information provided during account registration and verification</li>
                <li>Tenant rental history and behaviour reports</li>
                <li>Communications with our support team</li>
              </ul>
              <p className="text-sm text-stone-700 leading-relaxed">
                This policy does not apply to third-party websites or services linked from our platform.
              </p>
            </section>

            {/* 3. Platform Role and Nature of Information */}
            <section id="platform-role" className="mb-9 scroll-mt-24">
              <h2 className="text-lg font-bold text-stone-900 mb-3 pb-2 border-b border-stone-200">3. Platform Role and Nature of Information</h2>
              <p className="text-sm text-stone-700 leading-relaxed mb-3">
                Tenantra operates as a platform that facilitates identity-verified, consent-based rental history records submitted by landlords.
              </p>
              <p className="text-sm text-stone-700 leading-relaxed mb-3">
                Information on the platform includes user-submitted data relating to rental relationships and post-tenancy reporting.
              </p>
              <p className="text-sm text-stone-700 leading-relaxed mb-3">
                Tenantra does not independently verify all submitted information and does not act as a government registry, land title authority, or credit reporting agency.
              </p>
              <p className="text-sm text-stone-700 leading-relaxed">
                The platform provides structured rental records supported by identity verification, tenant consent, and dispute mechanisms.
              </p>
            </section>

            {/* 4. Information We Collect */}
            <section id="information-we-collect" className="mb-9 scroll-mt-24">
              <h2 className="text-lg font-bold text-stone-900 mb-3 pb-2 border-b border-stone-200">4. Information We Collect</h2>
              <p className="text-sm text-stone-700 leading-relaxed mb-4">We collect the following categories of personal information:</p>

              <h3 className="text-sm font-bold text-stone-800 mb-2">Account Information</h3>
              <ul className="list-disc pl-5 text-sm text-stone-700 space-y-1.5 mb-5">
                <li>Full legal name</li>
                <li>Email address</li>
                <li>Phone number</li>
                <li>Date of birth</li>
                <li>Password (encrypted)</li>
              </ul>

              <h3 className="text-sm font-bold text-stone-800 mb-2">Landlord Information</h3>
              <ul className="list-disc pl-5 text-sm text-stone-700 space-y-1.5 mb-5">
                <li>Supporting documentation related to property or tenancy</li>
                <li>Business name and address</li>
                <li>Government-issued identification</li>
                <li>Property details and addresses</li>
              </ul>

              <h3 className="text-sm font-bold text-stone-800 mb-2">Tenant Information</h3>
              <ul className="list-disc pl-5 text-sm text-stone-700 space-y-1.5 mb-5">
                <li>Rental history and tenancy records</li>
                <li>Payment behaviour (on-time, late, missed)</li>
                <li>Lease compliance status</li>
                <li>Property care assessments</li>
                <li>Dispute records</li>
              </ul>

              <h3 className="text-sm font-bold text-stone-800 mb-2">Technical Information</h3>
              <ul className="list-disc pl-5 text-sm text-stone-700 space-y-1.5">
                <li>IP address and browser type</li>
                <li>Device information</li>
                <li>Usage data and analytics</li>
                <li>Cookies and similar technologies</li>
              </ul>
            </section>

            {/* 5. How and Why We Collect Information */}
            <section id="how-we-collect" className="mb-9 scroll-mt-24">
              <h2 className="text-lg font-bold text-stone-900 mb-3 pb-2 border-b border-stone-200">5. How and Why We Collect Information</h2>
              <p className="text-sm text-stone-700 leading-relaxed mb-4">We collect personal information for the following purposes:</p>

              <h3 className="text-sm font-bold text-stone-800 mb-2">Service Delivery</h3>
              <ul className="list-disc pl-5 text-sm text-stone-700 space-y-1.5 mb-5">
                <li>Creating and managing accounts</li>
                <li>Generating rental history records</li>
                <li>Facilitating landlord-tenant onboarding and verification</li>
              </ul>

              <h3 className="text-sm font-bold text-stone-800 mb-2">Identity Verification</h3>
              <ul className="list-disc pl-5 text-sm text-stone-700 space-y-1.5 mb-5">
                <li>Verifying landlord identity</li>
                <li>Supporting accountability and fraud prevention</li>
              </ul>

              <h3 className="text-sm font-bold text-stone-800 mb-2">Platform Improvement</h3>
              <ul className="list-disc pl-5 text-sm text-stone-700 space-y-1.5 mb-5">
                <li>Analyzing usage patterns</li>
                <li>Improving features and functionality</li>
              </ul>

              <h3 className="text-sm font-bold text-stone-800 mb-2">Legal Compliance</h3>
              <ul className="list-disc pl-5 text-sm text-stone-700 space-y-1.5">
                <li>Complying with laws and regulations</li>
                <li>Responding to legal requests</li>
              </ul>
            </section>

            {/* 6. Consent */}
            <section id="consent" className="mb-9 scroll-mt-24">
              <h2 className="text-lg font-bold text-stone-900 mb-3 pb-2 border-b border-stone-200">6. Consent</h2>
              <p className="text-sm text-stone-700 leading-relaxed mb-4">We obtain consent in the following ways:</p>

              <h3 className="text-sm font-bold text-stone-800 mb-2">Express Consent</h3>
              <ul className="list-disc pl-5 text-sm text-stone-700 space-y-1.5 mb-5">
                <li>Account registration</li>
                <li>Tenant onboarding approval</li>
                <li>Identity verification authorization</li>
              </ul>

              <h3 className="text-sm font-bold text-stone-800 mb-2">Implied Consent</h3>
              <ul className="list-disc pl-5 text-sm text-stone-700 space-y-1.5 mb-5">
                <li>Voluntary use of the platform</li>
              </ul>

              <h3 className="text-sm font-bold text-stone-800 mb-2">Withdrawal of Consent</h3>
              <p className="text-sm text-stone-700 leading-relaxed mb-2">
                Users may withdraw consent by contacting <a href="mailto:privacy@tenantra.ca" className="text-teal-700 underline">privacy@tenantra.ca</a>.
              </p>
              <p className="text-sm text-stone-700 leading-relaxed">
                Withdrawal may limit platform functionality.
              </p>
            </section>

            {/* 7. Disputes and Corrections */}
            <section id="disputes-corrections" className="mb-9 scroll-mt-24">
              <h2 className="text-lg font-bold text-stone-900 mb-3 pb-2 border-b border-stone-200">7. Disputes and Corrections</h2>
              <p className="text-sm text-stone-700 leading-relaxed mb-4">
                Tenants may dispute rental reports they believe are inaccurate, misleading, or incomplete.
              </p>
              <p className="text-sm text-stone-700 leading-relaxed mb-2">When a dispute is raised:</p>
              <ul className="list-disc pl-5 text-sm text-stone-700 space-y-1.5 mb-4">
                <li>The report may be flagged</li>
                <li>Relevant information may be reviewed</li>
                <li>Appropriate action may be taken, including updating or restricting content</li>
              </ul>
              <p className="text-sm text-stone-700 leading-relaxed">
                Tenantra reserves the right to determine outcomes based on available information and platform policies.
              </p>
            </section>

            {/* 8. Third-Party Disclosure */}
            <section id="third-party-disclosure" className="mb-9 scroll-mt-24">
              <h2 className="text-lg font-bold text-stone-900 mb-3 pb-2 border-b border-stone-200">8. Third-Party Disclosure</h2>
              <p className="text-sm text-stone-700 leading-relaxed mb-4">We may share personal information with:</p>

              <h3 className="text-sm font-bold text-stone-800 mb-2">Service Providers</h3>
              <ul className="list-disc pl-5 text-sm text-stone-700 space-y-1.5 mb-5">
                <li>Didit &mdash; identity verification (document and biometric checks)</li>
                <li>Postmark &mdash; email delivery</li>
                <li>Vonage &mdash; SMS services</li>
                <li>Cloud hosting providers</li>
              </ul>

              <h3 className="text-sm font-bold text-stone-800 mb-2">Landlords</h3>
              <p className="text-sm text-stone-700 leading-relaxed mb-5">
                Rental records and reports, subject to consent and platform processes.
              </p>

              <h3 className="text-sm font-bold text-stone-800 mb-2">Legal Authorities</h3>
              <p className="text-sm text-stone-700 leading-relaxed mb-4">
                When required by law.
              </p>

              <p className="text-sm text-stone-700 leading-relaxed font-medium">
                We do not sell personal information.
              </p>
            </section>

            {/* 9. Data Storage and Security */}
            <section id="data-storage-security" className="mb-9 scroll-mt-24">
              <h2 className="text-lg font-bold text-stone-900 mb-3 pb-2 border-b border-stone-200">9. Data Storage and Security</h2>
              <p className="text-sm text-stone-700 leading-relaxed mb-4">We implement security measures including:</p>
              <ul className="list-disc pl-5 text-sm text-stone-700 space-y-1.5 mb-4">
                <li>Encryption (TLS and AES-256)</li>
                <li>Multi-factor authentication</li>
                <li>Access controls</li>
                <li>Security monitoring</li>
              </ul>
              <p className="text-sm text-stone-700 leading-relaxed">
                All data is stored in Canada unless otherwise required with safeguards.
              </p>
            </section>

            {/* 10. Data Retention */}
            <section id="data-retention" className="mb-9 scroll-mt-24">
              <h2 className="text-lg font-bold text-stone-900 mb-3 pb-2 border-b border-stone-200">10. Data Retention</h2>
              <p className="text-sm text-stone-700 leading-relaxed mb-4">We retain data as follows:</p>
              <div className="border border-stone-200 rounded-lg overflow-hidden mb-4">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="bg-stone-50">
                      <th className="text-left px-4 py-2.5 font-bold text-stone-800 border-b border-stone-200">Data Category</th>
                      <th className="text-left px-4 py-2.5 font-bold text-stone-800 border-b border-stone-200">Retention Period</th>
                    </tr>
                  </thead>
                  <tbody className="text-stone-700">
                    <tr className="border-b border-stone-100">
                      <td className="px-4 py-2.5">Account data</td>
                      <td className="px-4 py-2.5">Duration of account plus 2 years</td>
                    </tr>
                    <tr className="border-b border-stone-100">
                      <td className="px-4 py-2.5">Rental reports</td>
                      <td className="px-4 py-2.5">Up to 7 years</td>
                    </tr>
                    <tr>
                      <td className="px-4 py-2.5">Dispute records</td>
                      <td className="px-4 py-2.5">Retained as required</td>
                    </tr>
                  </tbody>
                </table>
              </div>
              <p className="text-sm text-stone-700 leading-relaxed">
                Data is securely deleted after retention periods.
              </p>
            </section>

            {/* 11. Your Rights Under PIPEDA */}
            <section id="your-rights" className="mb-9 scroll-mt-24">
              <h2 className="text-lg font-bold text-stone-900 mb-3 pb-2 border-b border-stone-200">11. Your Rights Under PIPEDA</h2>
              <p className="text-sm text-stone-700 leading-relaxed mb-4">You have the right to:</p>
              <ul className="list-disc pl-5 text-sm text-stone-700 space-y-1.5 mb-4">
                <li><strong>Access</strong> your data</li>
                <li><strong>Request corrections</strong> to inaccurate information</li>
                <li><strong>Withdraw consent</strong> for certain uses of your information</li>
                <li><strong>File complaints</strong> with our Privacy Officer or the Privacy Commissioner of Canada</li>
              </ul>
              <p className="text-sm text-stone-700 leading-relaxed">
                To exercise your rights, contact: <a href="mailto:privacy@tenantra.ca" className="text-teal-700 underline">privacy@tenantra.ca</a>
              </p>
            </section>

            {/* 12. Cookies and Tracking */}
            <section id="cookies-tracking" className="mb-9 scroll-mt-24">
              <h2 className="text-lg font-bold text-stone-900 mb-3 pb-2 border-b border-stone-200">12. Cookies and Tracking</h2>
              <p className="text-sm text-stone-700 leading-relaxed mb-3">We use cookies for:</p>
              <ul className="list-disc pl-5 text-sm text-stone-700 space-y-1.5 mb-4">
                <li>Authentication</li>
                <li>Platform functionality</li>
                <li>Analytics</li>
              </ul>
              <p className="text-sm text-stone-700 leading-relaxed">
                You can control cookies through your browser settings.
              </p>
            </section>

            {/* 13. Use of Information and Decision-Making */}
            <section id="use-of-information" className="mb-9 scroll-mt-24">
              <h2 className="text-lg font-bold text-stone-900 mb-3 pb-2 border-b border-stone-200">13. Use of Information and Decision-Making</h2>
              <p className="text-sm text-stone-700 leading-relaxed mb-3">
                Information provided through Tenantra is intended to support decision-making by landlords.
              </p>
              <div className="bg-stone-50 border-l-4 border-stone-800 px-4 py-3 mb-3">
                <p className="text-sm text-stone-800 font-semibold">
                  Tenantra is not responsible for decisions made based on platform data or interpretations of rental records.
                </p>
              </div>
              <p className="text-sm text-stone-700 leading-relaxed">
                Users are responsible for how they use information obtained through the platform.
              </p>
            </section>

            {/* 14. SMS Text Messaging */}
            <section id="sms-messaging" className="mb-9 scroll-mt-24" data-testid="privacy-sms-section">
              <h2 className="text-lg font-bold text-stone-900 mb-3 pb-2 border-b border-stone-200">14. SMS Text Messaging</h2>
              <p className="text-sm text-stone-700 leading-relaxed mb-3">
                If you opt in to receive text messages from Tenantra, we use your mobile number only to send account notifications, including verification codes, rent reminders, payment confirmations, and lease alerts. Message frequency varies. Message and data rates may apply.
              </p>
              <div className="bg-stone-50 border-l-4 border-stone-800 px-4 py-3 mb-3">
                <p className="text-sm text-stone-800 font-semibold">
                  No mobile information will be shared with third parties or affiliates for marketing or promotional purposes. Text messaging originator opt-in data and consent will not be shared with any third parties, except service providers that deliver messages on our behalf.
                </p>
              </div>
              <ul className="list-disc pl-5 text-sm text-stone-700 space-y-1.5">
                <li>Reply <strong>STOP</strong> at any time to opt out. You will receive one confirmation message and no further texts.</li>
                <li>Reply <strong>HELP</strong> for assistance, or contact <a href="mailto:support@tenantra.ca" className="text-teal-700 underline">support@tenantra.ca</a>.</li>
                <li>Consent to receive text messages is not a condition of using Tenantra.</li>
              </ul>
            </section>

            {/* 15. Changes to This Policy */}
            <section id="changes" className="mb-9 scroll-mt-24">
              <h2 className="text-lg font-bold text-stone-900 mb-3 pb-2 border-b border-stone-200">15. Changes to This Policy</h2>
              <p className="text-sm text-stone-700 leading-relaxed mb-3">
                We may update this Privacy Policy periodically.
              </p>
              <p className="text-sm text-stone-700 leading-relaxed">
                Continued use of the platform constitutes acceptance of any updates.
              </p>
            </section>

            {/* 16. Contact Us */}
            <section id="contact-us" className="mb-9 scroll-mt-24">
              <h2 className="text-lg font-bold text-stone-900 mb-3 pb-2 border-b border-stone-200">16. Contact Us</h2>
              <p className="text-sm text-stone-700 leading-relaxed mb-4">
                For questions or concerns about this Privacy Policy or our privacy practices:
              </p>
              <div className="bg-stone-50 border border-stone-200 rounded-lg p-5 text-sm text-stone-700 mb-4">
                <p className="font-bold text-stone-800 mb-1">Privacy Officer</p>
                <p>Tenantra Inc.</p>
                <p>Toronto, Ontario, Canada</p>
                <p className="mt-2">Email: <a href="mailto:privacy@tenantra.ca" className="text-teal-700 underline">privacy@tenantra.ca</a></p>
              </div>
              <div className="bg-stone-50 border border-stone-200 rounded-lg p-5 text-sm text-stone-700">
                <p className="font-bold text-stone-800 mb-1">Office of the Privacy Commissioner of Canada</p>
                <p>If you are not satisfied with our response, you may contact the Office of the Privacy Commissioner of Canada.</p>
                <p className="mt-2">30 Victoria Street, Gatineau, Quebec K1A 1H3</p>
                <p>Toll-free: 1-800-282-1376</p>
                <p>Website: <a href="https://www.priv.gc.ca" target="_blank" rel="noopener noreferrer" className="text-teal-700 underline">www.priv.gc.ca</a></p>
              </div>
            </section>

            {/* Document footer */}
            <div className="pt-6 mt-6 border-t border-stone-200 text-center">
              <p className="text-xs text-stone-400">
                &copy; {new Date().getFullYear()} Tenantra Inc. All rights reserved. &mdash; PIPEDA Compliant.
              </p>
            </div>
          </article>
        </div>
      </div>

      {/* Print Styles */}
      <style>{`
        @media print {
          body { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
          .print\\:hidden { display: none !important; }
          @page { margin: 0.75in; size: letter; }
        }
      `}</style>
    </div>
  );
}
